import { useState } from "react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { toast } from "sonner";
import { api, formatApiErrorDetail, formatMoney } from "@/lib/api";

const money = (n) => formatMoney(n || 0);
const cents = (n) => `$${(n || 0).toFixed(2)}`;

export const QuoteBar = ({ pid, quote, setQuote }) => {
  const [open, setOpen] = useState(false);
  const [billing, setBilling] = useState(false);
  const [note, setNote] = useState("");
  if (!quote) return null;
  const bill = async () => {
    setBilling(true); setNote("");
    try { const r = await api.post(`/admin/studio/projects/${pid}/bill-cost`); setQuote(r.data.quote); toast.success(`Added $${r.data.billed.toFixed(2)} to the client's Draft invoice`); }
    catch (e) { setNote(formatApiErrorDetail(e.response?.data?.detail)); }
    setBilling(false);
  };
  const save = async (body) => {
    try { setQuote((await api.put(`/admin/studio/projects/${pid}/quote`, body)).data); } catch { toast.error("Couldn't save the quote"); }
  };
  const addonsMap = Object.fromEntries(quote.addons.map((a) => [a.key, a.on]));
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button data-testid="quote-bar" className="hidden items-center gap-1.5 whitespace-nowrap rounded-full border border-white/10 px-3 py-1.5 font-mono-tech text-[11px] text-slate-300 hover:border-cyan-400/40 lg:inline-flex">
          AI cost <span data-testid="quote-ai-cost" className="text-white">{cents(quote.ai_cost)}</span> · Quote <span data-testid="quote-total" className="text-white">{money(quote.total)}</span> · Profit <span data-testid="quote-profit" className="text-emerald-300">{money(quote.profit)}</span>
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" data-testid="quote-breakdown" className="w-80 border-white/10 bg-[#0D111A] text-sm text-slate-200">
        <p className="mb-2 text-xs text-slate-500">Quote breakdown · AI cost is estimated</p>
        <label className="flex items-center justify-between gap-2">
          <span>Base</span>
          <select value={quote.base.overridden ? quote.base.key : ""} onChange={(e) => save({ base: e.target.value || null, addons: addonsMap })} data-testid="quote-base-select"
            className="rounded border border-white/10 bg-black/40 px-2 py-1 text-xs text-white">
            <option value="">Auto ({quote.bases.find((b) => b.key === quote.base.auto)?.name})</option>
            {quote.bases.map((b) => <option key={b.key} value={b.key}>{b.name}</option>)}
          </select>
        </label>
        <p className="mt-1 flex justify-between text-xs text-slate-400"><span>{quote.base.name}</span><span data-testid="quote-base-price">{money(quote.base.price)}</span></p>
        <p className="mt-1 flex justify-between text-xs text-slate-400"><span>{quote.pages} pages ({quote.included_pages} included) · {quote.extra_pages} extra × {money(quote.extra_page_rate)}</span><span data-testid="quote-extra-cost">{money(quote.extra_cost)}</span></p>
        <ul className="mt-3 space-y-1.5 border-t border-white/5 pt-2">
          {quote.addons.map((a) => (
            <li key={a.key} className="flex items-center gap-2 text-xs">
              <input type="checkbox" checked={a.on} onChange={(e) => save({ base: quote.base.overridden ? quote.base.key : null, addons: { ...addonsMap, [a.key]: e.target.checked } })} data-testid={`quote-addon-${a.key}`} className="accent-cyan-400" />
              <span className="flex-1">{a.label}{a.detected ? <span className="ml-1 text-[10px] text-cyan-300">detected</span> : ""}</span><span>{money(a.price)}</span>
            </li>
          ))}
        </ul>
        <div className="mt-3 space-y-1 border-t border-white/5 pt-2 text-xs">
          <p className="flex justify-between font-semibold text-white"><span>Quote total</span><span>{money(quote.total)}</span></p>
          <p className="flex justify-between text-slate-400"><span>AI cost (estimated)</span><span>{cents(quote.ai_cost)}</span></p>
          <p className="flex justify-between text-emerald-300"><span>Profit</span><span>{money(quote.profit)}</span></p>
        </div>
        <div className="mt-3 border-t border-white/5 pt-2 text-xs">
          <p className="flex justify-between text-slate-400"><span>Unbilled building cost{quote.markup_pct ? ` (+${quote.markup_pct}% markup)` : ""}</span><span data-testid="quote-billable">{cents(quote.billable)}</span></p>
          <button onClick={bill} disabled={billing || quote.billable < 0.01} data-testid="quote-bill-cost" className="mt-2 w-full rounded-full border border-cyan-400/40 py-1.5 text-xs font-semibold text-cyan-200 hover:bg-cyan-400/10 disabled:opacity-50">Bill building cost</button>
          {(note || quote.billable < 0.01) && <p data-testid="quote-bill-note" className="mt-1.5 text-center text-[11px] text-amber-200">{note || "All building cost billed"}</p>}
        </div>
      </PopoverContent>
    </Popover>
  );
};
